import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useCallback, useEffect, useState } from 'react';

/** Tipo de filtro por producto: in, not_in, is_null, is_not_null */
export type CartProductFilterAction = 'in' | 'not_in' | 'is_null' | 'is_not_null';

/** Estado local de filtros para carritos */
export interface CartsFiltersState {
  /** Tipo de filtro por producto (vacío = sin filtro) */
  productFilterAction: CartProductFilterAction | '';
  /** ID del producto (para in/not_in) */
  productId: string;
}

const defaultState: CartsFiltersState = {
  productFilterAction: '',
  productId: '',
};

/**
 * Parsea condiciones iniciales (desde URL) al estado local del panel.
 */
function parseConditions(conditions: FilterConditionOutput[]): CartsFiltersState {
  const state = { ...defaultState };

  for (const c of conditions) {
    if (c.column !== 'product_id') continue;

    if (c.action === 'in' || c.action === 'not_in') {
      state.productFilterAction = c.action;
      const val = c.value;
      if (Array.isArray(val) && val.length > 0) {
        state.productId = String(val[0]);
      } else if (val != null) {
        state.productId = String(val);
      }
    } else if (c.action === 'is_null') {
      state.productFilterAction = 'is_null';
      state.productId = '';
    } else if (c.action === 'is_not_null') {
      state.productFilterAction = 'is_not_null';
      state.productId = '';
    }
  }

  return state;
}

/**
 * Convierte el estado local a condiciones para la API (FilterConditionOutput).
 */
function convertToConditions(state: CartsFiltersState): FilterConditionOutput[] {
  const conditions: FilterConditionOutput[] = [];

  if (state.productFilterAction === 'in' || state.productFilterAction === 'not_in') {
    if (state.productId) {
      conditions.push({
        column: 'product_id',
        action: state.productFilterAction,
        value: [state.productId],
      });
    }
  } else if (state.productFilterAction === 'is_null') {
    conditions.push({
      column: 'product_id',
      action: 'is_null',
      value: null,
    });
  } else if (state.productFilterAction === 'is_not_null') {
    conditions.push({
      column: 'product_id',
      action: 'is_not_null',
      value: null,
    });
  }

  return conditions;
}

export interface UseCartsFiltersParams {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

/**
 * Hook para gestionar el estado de filtros del módulo Carritos.
 */
export const useCartsFilters = ({ initialConditions = [], onChange }: UseCartsFiltersParams) => {
  const [filters, setFilters] = useState<CartsFiltersState>(() => parseConditions(initialConditions));

  useEffect(() => {
    setFilters(parseConditions(initialConditions));
  }, [initialConditions]);

  const notifyChange = useCallback(
    (next: CartsFiltersState) => {
      onChange?.(convertToConditions(next));
    },
    [onChange],
  );

  const handleProductFilterTypeChange = useCallback(
    (value: string) => {
      const action = (value === '' ? '' : value) as CartsFiltersState['productFilterAction'];
      setFilters((prev) => {
        const next = {
          ...prev,
          productFilterAction: action,
          productId: action === 'in' || action === 'not_in' ? prev.productId : '',
        };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const handleProductChange = useCallback(
    (value: string) => {
      setFilters((prev) => {
        const next = { ...prev, productId: value || '' };
        notifyChange(next);
        return next;
      });
    },
    [notifyChange],
  );

  const clearAllFilters = useCallback(() => {
    setFilters(defaultState);
    notifyChange(defaultState);
  }, [notifyChange]);

  return {
    filters,
    handleProductFilterTypeChange,
    handleProductChange,
    clearAllFilters,
  };
};
